// =============================================================================
// ALERTES SYSTÈME
// -----------------------------------------------------------------------------
// Affiche une vraie notification du téléphone / de l'ordinateur quand un
// nouvel événement arrive (Amen, commentaire, message, demande d'amitié…),
// même si l'application est en arrière-plan.
//
// ⚠️ Portée exacte : ceci fonctionne tant que l'application tourne, même
// masquée. Pour être prévenu **application fermée**, il faut du « push »
// serveur (Firebase Cloud Messaging) — voir README § notifications.
// =============================================================================

import { icone } from "./icones.js";

const CLE_PREF = "kn.alertes";
const CLE_VUES = "kn.alertes.vues";

/* ------------------------------------------------------- deux mondes, une API */
// Dans l'app Android, le WebView refuse l'API du navigateur : on passe alors par
// le module Capacitor, qui parle au vrai gestionnaire de notifications d'Android.
// Le reste de l'application ne voit qu'une seule interface, identique.

const plugin = () =>
  window.Capacitor?.isNativePlatform?.() ? window.Capacitor.Plugins?.LocalNotifications : null;

let etatNatif = "default"; // mémorisé, car le module répond de façon asynchrone
let compteur = 1;

export const supporte = () => Boolean(plugin()) || "Notification" in window;

export function autorisation() {
  if (plugin()) return etatNatif;
  return "Notification" in window ? Notification.permission : "unsupported";
}

/**
 * À appeler une fois au démarrage : relit l'autorisation côté Android et
 * branche l'ouverture de l'app quand on touche une notification.
 */
export async function initialiser() {
  const p = plugin();
  if (!p) return;
  try {
    const { display } = await p.checkPermissions();
    etatNatif = display;
    await p.addListener("localNotificationActionPerformed", (e) => {
      const lien = e?.notification?.extra?.lien;
      if (lien) location.hash = lien;
    });
  } catch {
    /* module indisponible : on reste sur le comportement du navigateur */
  }
}

/** L'utilisateur a-t-il activé les alertes sur cet appareil ? */
export function activees() {
  return supporte() && autorisation() === "granted" && localStorage.getItem(CLE_PREF) !== "non";
}

/** Demande l'autorisation au navigateur. À n'appeler que sur un geste de l'utilisateur. */
export async function demander() {
  const p = plugin();
  if (p) {
    const { display } = await p.requestPermissions();
    etatNatif = display;
    if (display === "granted") localStorage.setItem(CLE_PREF, "oui");
    return display;
  }
  if (!("Notification" in window)) throw new Error("Cet appareil ne gère pas les alertes.");
  const reponse = await Notification.requestPermission();
  if (reponse === "granted") localStorage.setItem(CLE_PREF, "oui");
  return reponse;
}

export function desactiver() {
  localStorage.setItem(CLE_PREF, "non");
}

/* ------------------------------------------------------- mémoire des vues */
// On ne veut pas ré-alerter pour un événement déjà signalé : au rechargement
// de la page, l'abonnement renvoie tout l'historique.

const lireVues = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(CLE_VUES) || "[]"));
  } catch {
    return new Set();
  }
};

const ecrireVues = (ensemble) => {
  // on ne garde que les 200 derniers identifiants : inutile de tout conserver
  localStorage.setItem(CLE_VUES, JSON.stringify([...ensemble].slice(-200)));
};

/** Marque un lot d'événements comme « déjà connus », sans alerter. */
export function amorcer(ids) {
  const vues = lireVues();
  ids.forEach((id) => vues.add(id));
  ecrireVues(vues);
}

/* --------------------------------------------------------------- affichage */

const TEXTES = {
  amen: (n) => `${n.deNom} a soutenu votre publication`,
  priere: (n) => `${n.deNom} prie pour votre demande`,
  commentaire: (n) => `${n.deNom} a commenté votre publication`,
  partage: (n) => `${n.deNom} a partagé votre publication`,
  message: (n) => `${n.deNom} vous a envoyé un message`,
  demande_ami: (n) => `${n.deNom} souhaite devenir votre ami`,
  ami_accepte: (n) => `${n.deNom} a accepté votre demande d'amitié`,
  story: (n) => `${n.deNom} a publié un statut`,
};

const lienDe = (n) =>
  n.type === "message" && n.convoId ? `#/messages/${n.convoId}` : n.postId ? `#/publication/${n.postId}` : "#/notifications";

/**
 * Alerte pour les nouveautés d'une liste de notifications.
 * Ne signale que ce qui n'a jamais été signalé, et rien si la page est au premier plan.
 */
export function signaler(notifications) {
  const vues = lireVues();
  const nouvelles = notifications.filter((n) => !n.lu && !vues.has(n.id));
  nouvelles.forEach((n) => vues.add(n.id));
  ecrireVues(vues);

  if (!activees() || document.visibilityState === "visible") return 0;

  // au-delà de trois, on résume : personne ne veut dix bandeaux d'un coup
  if (nouvelles.length > 3) {
    afficher(`${nouvelles.length} nouvelles notifications`, "Ouvrez Koinonia pour les voir", "#/notifications");
    return nouvelles.length;
  }
  for (const n of nouvelles) {
    const titre = (TEXTES[n.type] || ((x) => `${x.deNom} a interagi avec vous`))(n);
    afficher(titre, n.apercu || "", lienDe(n));
  }
  return nouvelles.length;
}

/** Crée l'alerte et ramène l'application au bon endroit quand on clique dessus. */
export function afficher(titre, corps, lien) {
  const p = plugin();
  if (p) {
    // côté Android : une vraie notification système, qui survit à l'app réduite
    p.schedule({
      notifications: [{ id: compteur++, title: titre, body: corps || "", extra: { lien } }],
    }).catch(() => {});
    return null;
  }
  try {
    const alerte = new Notification(titre, {
      body: corps,
      icon: "icons/icon-192.png",
      badge: "icons/icon-192.png",
      tag: lien, // deux alertes pour le même endroit se remplacent
      lang: "fr",
    });
    alerte.onclick = () => {
      window.focus();
      if (lien) location.hash = lien;
      alerte.close();
    };
    return alerte;
  } catch {
    return null; // navigateur trop restrictif : on n'insiste pas
  }
}
